import { useSettings } from "context/SettingsContext";
import { t } from "context/translations";
import {
  CONTACT_INFO,
  getTelHref,
  getWhatsAppHref,
} from "constants/contactInfo";
import {
  FaFacebookF,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaWhatsapp,
  FaYoutube,
  FaRegClock,
} from "react-icons/fa";

// const YOUTUBE_URL = "https://www.youtube.com/@nabilfashion17";
const MAIN_PHONE = CONTACT_INFO.phone.display;
const HELPLINE = CONTACT_INFO.helpline.display;
const FACEBOOK_URL = CONTACT_INFO.social.facebookUrl;
const WHATSAPP_URL = getWhatsAppHref(
  CONTACT_INFO.phone.whatsapp,
  CONTACT_INFO.whatsappMessage,
);

const WORKING_HOURS = "Every day: 9:00 AM - 10:00 PM";

const contactItems = [
  {
    title: "Phone",
    value: MAIN_PHONE,
    subValue: HELPLINE,
    href: getTelHref(MAIN_PHONE),
    icon: FaPhoneAlt,
    accent: "from-sky-500 to-cyan-400",
    ring: "ring-sky-500/20",
  },
  {
    title: "Facebook",
    value: "Follow us for updates",
    subValue: "Fast replies and product posts",
    href: FACEBOOK_URL,
    icon: FaFacebookF,
    accent: "from-primary-600 to-primary-500",
    ring: "ring-primary-500/20",
  },
  {
    title: "Address",
    value:
      "Bonolota Market, Shop no-5, 1st Floor, Dhaka New Market, Dhaka 1205",
    subValue: "Bonolota Super Market, Dhaka 1205",
    href: "https://www.google.com/maps/search/?api=1&query=17,+1st+Floor,+New+Market+Kacha+bazar,+Bonolota+Super+Market,+Dhaka+1205",
    icon: FaMapMarkerAlt,
    accent: "from-rose-500 to-orange-400",
    ring: "ring-rose-500/20",
  },
  {
    title: "Working Hours",
    value: WORKING_HOURS,
    subValue: "We usually respond as quickly as possible",
    icon: FaRegClock,
    accent: "from-emerald-500 to-teal-400",
    ring: "ring-emerald-500/20",
  },
  // {
  //   title: "YouTube",
  //   value: "Watch our latest videos",
  //   subValue: "Product demos and updates",
  //   href: YOUTUBE_URL,
  //   icon: FaYoutube,
  //   accent: "from-red-600 to-rose-500",
  //   ring: "ring-red-500/20",
  // },
];

const ContactCard = ({ item, isDark }) => {
  const Icon = item.icon;
  const cardInner = (
    <div
      className={`group h-full rounded-lg border p-5  transition duration-300  ${
        isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center gap-4">
        <div
          className={`flex md:h-14 md:w-14 h-8 w-8 flex-none items-center justify-center rounded bg-gradient-to-br ${item.accent} text-white shadow-lg `}
        >
          <Icon className="md:h-6 md:w-6 h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            {item.title}
          </p>
          <h3 className="text-sm  font-semibold text-slate-900 dark:text-white">
            {item.value}
          </h3>
        </div>
      </div>
    </div>
  );

  if (!item.href) return cardInner;

  return (
    <a
      href={item.href}
      target={item.href.startsWith("tel:") ? undefined : "_blank"}
      rel={item.href.startsWith("tel:") ? undefined : "noreferrer"}
    >
      {cardInner}
    </a>
  );
};

const ContactUs = () => {
  const { theme, language } = useSettings();
  const isDark = theme === "dark";

  const phoneDial = getTelHref(MAIN_PHONE);

  return (
    <div
      className={`relative overflow-hidden select-none ${
        isDark ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-900"
      }`}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 left-0 h-72 w-72 rounded-lg bg-sky-500/15 blur-3xl" />
        <div className="absolute top-28 right-0 h-80 w-80 rounded-lg bg-emerald-400/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-60 w-60 rounded-lg bg-rose-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-2 py-1 sm:px-5 md:px-6 md:py-8">
        <section
          className={`overflow-hidden rounded-lg border shadow-2xl ${
            isDark
              ? "border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white"
              : "border-white bg-gradient-to-br from-slate-900 via-sky-900 to-slate-800 text-white"
          }`}
        >
          <div className="px-5 py-5 sm:px-6 md:px-10 md:py-12">
            <div className="flex flex-col justify-center">
              <h1 className=" text-xl text-center font-bold leading-tight sm:text-3xl md:text-5xl">
                Contact with Nabil Fashion
              </h1>

              <p className="md:mt-4 mt-3 text-center text-xs md:leading-7 leading-6 text-slate-200 sm:mt-5 md:text-base">
                Reach us for product questions, orders, delivery support, or
                quick help. We keep our contact points clear so customers can
                get in touch fast.
              </p>
              <div className="md:mt-6 mt-3 items-center justify-center flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">
                  Fast response
                </span>
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">
                  Friendly guidance
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 md:mt-10">
          {/*<div className="flex items-end justify-between gap-4">*/}
          {/*  <div>*/}
          {/*    <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-500">*/}
          {/*      Contact details*/}
          {/*    </p>*/}
          {/*    <h2 className="mt-2 text-2xl font-bold md:text-3xl">*/}
          {/*      Everything in one place*/}
          {/*    </h2>*/}
          {/*  </div>*/}
          {/*</div>*/}

          <div className="mt-6 grid md:gap-4 gap-2 md:grid-cols-2 xl:grid-cols-3">
            {contactItems.map((item) => (
              <ContactCard key={item.title} item={item} isDark={isDark} />
            ))}
          </div>
        </section>

        <section className="mt-6 md:mt-10 mb-16">
          <div
            className={`grid gap-4 rounded-lg border p-5 md:p-6 ${
              isDark
                ? "border-white/10 bg-white/5"
                : "border-slate-200 bg-white shadow-sm"
            }`}
          >
            <div>
              <p className="text-sm text-center font-semibold uppercase tracking-[0.28em] text-sky-500">
                Quick actions
              </p>
              <p className="mt-2 text-center text-sm md:leading-7 leading-5 text-slate-500 dark:text-slate-300">
                Use the buttons below to message us on WhatsApp or place a
                direct call right away.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:-translate-y-0.5 hover:bg-emerald-400"
              >
                <FaWhatsapp className="text-lg" />
                WhatsApp
              </a>
              <a
                href={phoneDial}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary-500 via-primary-600 to-primary-800 text-white px-5 py-3 text-sm font-semibold  shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100"
              >
                <FaPhoneAlt className="text-sm text-white" />
                {t("callNow", language)}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactUs;
